import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { formatLocalDate } from "../utils/dateUtils.js";

// Local storage keys for persisting values
const LS_SELECTED_RANGE = "dateRangeSelector_selectedRange";
const LS_CUSTOM_START_DATE = "dateRangeSelector_customStartDate";
const LS_CUSTOM_END_DATE = "dateRangeSelector_customEndDate";

const DateRangeSelector = ({
  onDateRangeChange,
  initialStartDate,
  initialEndDate,
}) => {
  // Initialize states from localStorage or fallback to provided values
  const [selectedRange, setSelectedRange] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_SELECTED_RANGE);
      return saved ? JSON.parse(saved) : "currentMonth";
    } catch (error) {
      console.error("Error loading saved date range:", error);
      return "currentMonth";
    }
  });

  const [customStartDate, setCustomStartDate] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_CUSTOM_START_DATE);
      return saved ? JSON.parse(saved) : initialStartDate || "";
    } catch (error) {
      console.error("Error loading saved start date:", error);
      return initialStartDate || "";
    }
  });

  const [customEndDate, setCustomEndDate] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_CUSTOM_END_DATE);
      return saved ? JSON.parse(saved) : initialEndDate || "";
    } catch (error) {
      console.error("Error loading saved end date:", error);
      return initialEndDate || "";
    }
  });

  // Debug: log when selectedRange changes
  console.log("DateRangeSelector render - selectedRange:", selectedRange);

  // Track selectedRange changes and save to localStorage
  useEffect(() => {
    console.log("selectedRange changed to:", selectedRange);
    try {
      localStorage.setItem(LS_SELECTED_RANGE, JSON.stringify(selectedRange));
    } catch (error) {
      console.error("Error saving date range:", error);
    }
  }, [selectedRange]);

  // Save custom dates to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(
        LS_CUSTOM_START_DATE,
        JSON.stringify(customStartDate),
      );
      localStorage.setItem(LS_CUSTOM_END_DATE, JSON.stringify(customEndDate));
    } catch (error) {
      console.error("Error saving custom dates:", error);
    }
  }, [customStartDate, customEndDate]);

  // Apply the saved date range when component mounts
  useEffect(() => {
    const dateRange = getDateRange(selectedRange);
    onDateRangeChange(dateRange);
  }, []);

  // Helper function to get date range based on selection
  const getDateRange = (rangeType) => {
    console.log("getDateRange called with:", rangeType);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (rangeType) {
      case "currentMonth": {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const result = {
          startDate: formatLocalDate(startOfMonth),
          endDate: formatLocalDate(today),
        };
        console.log("currentMonth result:", result);
        return result;
      }
      case "previousMonth": {
        const startOfPrevMonth = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        );
        const endOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          startDate: formatLocalDate(startOfPrevMonth),
          endDate: formatLocalDate(endOfPrevMonth),
        };
      }
      case "currentQuarter": {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const startOfQuarter = new Date(
          now.getFullYear(),
          currentQuarter * 3,
          1,
        );
        return {
          startDate: formatLocalDate(startOfQuarter),
          endDate: formatLocalDate(today),
        };
      }
      case "previousQuarter": {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const prevQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const prevQuarterYear =
          currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const startOfPrevQuarter = new Date(
          prevQuarterYear,
          prevQuarter * 3,
          1,
        );
        const endOfPrevQuarter = new Date(
          prevQuarterYear,
          prevQuarter * 3 + 3,
          0,
        );
        return {
          startDate: formatLocalDate(startOfPrevQuarter),
          endDate: formatLocalDate(endOfPrevQuarter),
        };
      }
      case "last30Days": {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return {
          startDate: formatLocalDate(thirtyDaysAgo),
          endDate: formatLocalDate(today),
        };
      }
      case "last90Days": {
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        return {
          startDate: formatLocalDate(ninetyDaysAgo),
          endDate: formatLocalDate(today),
        };
      }
      case "yearToDate": {
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return {
          startDate: formatLocalDate(startOfYear),
          endDate: formatLocalDate(today),
        };
      }
      case "custom": {
        return {
          startDate: customStartDate || null,
          endDate: customEndDate || null,
        };
      }
      default: {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          startDate: formatLocalDate(startOfMonth),
          endDate: formatLocalDate(today),
        };
      }
    }
  };

  const handleRangeChange = (rangeType) => {
    console.log("handleRangeChange called with:", rangeType);
    setSelectedRange(rangeType);

    // Calculate date range and notify parent
    const dateRange = getDateRange(rangeType);
    console.log("Date range calculated for", rangeType, ":", dateRange);
    onDateRangeChange(dateRange);
  };

  const handleCustomDateChange = () => {
    if (selectedRange === "custom") {
      const dateRange = getDateRange("custom");
      onDateRangeChange(dateRange);

      // Save the custom dates
      try {
        localStorage.setItem(
          LS_CUSTOM_START_DATE,
          JSON.stringify(customStartDate),
        );
        localStorage.setItem(LS_CUSTOM_END_DATE, JSON.stringify(customEndDate));
      } catch (error) {
        console.error("Error saving custom dates:", error);
      }
    }
  };

  const formatDateRange = (startDate, endDate) => {
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      return `From ${formatDate(startDate)}`;
    } else if (endDate) {
      return `Until ${formatDate(endDate)}`;
    }
    // This should not happen anymore since we always provide date ranges
    return "No date range";
  };

  return (
    <div className="bg-white p-3 rounded-lg shadow-sm mb-4">
      {/* Compact layout with inline buttons and custom range */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-700 mr-2">Filter:</span>

        <button
          onClick={() => handleRangeChange("currentMonth")}
          className={`px-2 py-1 text-xs rounded ${
            selectedRange === "currentMonth"
              ? "bg-purple-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          This Month
        </button>

        <button
          onClick={() => handleRangeChange("previousMonth")}
          className={`px-2 py-1 text-xs rounded ${
            selectedRange === "previousMonth"
              ? "bg-purple-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Last Month
        </button>

        <button
          onClick={() => handleRangeChange("last30Days")}
          className={`px-2 py-1 text-xs rounded ${
            selectedRange === "last30Days"
              ? "bg-purple-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          30 Days
        </button>

        <button
          onClick={() => handleRangeChange("last90Days")}
          className={`px-2 py-1 text-xs rounded ${
            selectedRange === "last90Days"
              ? "bg-purple-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          90 Days
        </button>

        <button
          onClick={() => handleRangeChange("yearToDate")}
          className={`px-2 py-1 text-xs rounded ${
            selectedRange === "yearToDate"
              ? "bg-purple-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          YTD
        </button>

        <button
          onClick={() => handleRangeChange("custom")}
          className={`px-2 py-1 text-xs rounded ${
            selectedRange === "custom"
              ? "bg-purple-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Custom
        </button>

        {/* Inline custom date inputs when custom is selected */}
        {selectedRange === "custom" && (
          <>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => {
                setCustomStartDate(e.target.value);
                setTimeout(handleCustomDateChange, 100);
              }}
              className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Start"
            />
            <span className="text-xs text-gray-500">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => {
                setCustomEndDate(e.target.value);
                setTimeout(handleCustomDateChange, 100);
              }}
              className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              placeholder="End"
            />
          </>
        )}

        {/* Current range display */}
        <span className="text-xs text-gray-500 ml-auto">
          {(() => {
            const currentRange = getDateRange(selectedRange);
            return formatDateRange(
              currentRange.startDate,
              currentRange.endDate,
            );
          })()}
        </span>
      </div>
    </div>
  );
};

DateRangeSelector.propTypes = {
  onDateRangeChange: PropTypes.func.isRequired,
  initialStartDate: PropTypes.string,
  initialEndDate: PropTypes.string,
};

export default DateRangeSelector;
